import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export interface StripePriceMapping {
  id: number;
  package_id: number;
  package_code: string;
  package_name: string | null;
  price_weekly: number | null;
  price_monthly: number | null;
  billing_cycle: string;
  stripe_product_id: string;
  stripe_price_id: string;
  price_amount: number;
  currency: string;
  created_at: string;
  updated_at: string;
  last_synced_at: string | null;
}

export function useStripePriceMappings() {
  return useQuery({
    queryKey: ['stripe-price-mappings'],
    queryFn: () => apiClient<StripePriceMapping[]>('/stripe-price-mappings'),
  });
}

function useInvalidateMappings() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['stripe-price-mappings'] });
}

export interface StripePriceMappingDraft {
  id?: number;
  package_id?: number;
  billing_cycle?: string;
  stripe_product_id?: string | null;
  stripe_price_id?: string | null;
  price_amount?: number;
  currency?: string;
}

export function useCreateStripePriceMapping() {
  const invalidate = useInvalidateMappings();
  return useMutation({
    mutationFn: (draft: StripePriceMappingDraft) =>
      apiClient<StripePriceMapping>('/stripe-price-mappings', { method: 'POST', body: JSON.stringify(draft) }),
    onSuccess: invalidate,
  });
}

export function useUpdateStripePriceMapping() {
  const invalidate = useInvalidateMappings();
  return useMutation({
    mutationFn: ({ id, ...fields }: StripePriceMappingDraft & { id: number }) =>
      apiClient<StripePriceMapping>(`/stripe-price-mappings/${id}`, { method: 'PUT', body: JSON.stringify(fields) }),
    onSuccess: invalidate,
  });
}

export function useDeleteStripePriceMapping() {
  const invalidate = useInvalidateMappings();
  return useMutation({
    mutationFn: (id: number) => apiClient<{ message: string }>(`/stripe-price-mappings/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

interface RefreshPricesResult {
  success: boolean;
  message: string;
  summary: { totalMappings: number; updated: number; unchanged: number; errors: number };
}

export function useRefreshStripePrices() {
  const invalidate = useInvalidateMappings();
  return useMutation({
    mutationFn: () => apiClient<RefreshPricesResult>('/stripe-price-mappings/refresh-prices', { method: 'POST' }),
    onSuccess: invalidate,
  });
}

interface SyncResult {
  success: boolean;
  productsCreated: number;
  pricesCreated: number;
  mappingsUpdated: number;
  errors: string[];
}

export function useSyncStripePriceMappings() {
  const invalidate = useInvalidateMappings();
  return useMutation({
    mutationFn: (forceRecreate: boolean) =>
      apiClient<SyncResult>('/stripe-price-mappings/sync', { method: 'POST', body: JSON.stringify({ forceRecreate }) }),
    onSuccess: invalidate,
  });
}

export interface ProductStatus {
  id: string;
  name: string;
  active: boolean;
  package_code: string;
  error?: string;
}

export function useStripeProductStatus() {
  return useQuery({
    queryKey: ['stripe-product-status'],
    queryFn: () => apiClient<{ productStatuses: Record<string, ProductStatus>; total: number }>('/stripe-products/status'),
  });
}

export function useToggleStripeProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, active }: { productId: string; active: boolean }) =>
      apiClient<{ product: ProductStatus; message: string }>(`/stripe-products/${productId}/toggle`, {
        method: 'POST',
        body: JSON.stringify({ active }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['stripe-product-status'] }),
  });
}
